// src/pages/Quotes.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import EnhancedQuoteCard from '../components/EnhancedQuoteCard';
import { ArrowLeft, ArrowLeftCircle, ArrowRightCircle, Loader2 } from 'lucide-react';

function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    pageSize: 10,
    totalQuotes: 0,
    hasMore: false
  });
  
  // Router hooks
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get pagination and filtering from URL
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const tagParam = searchParams.get('tag');
  
  // Memoize the URL update function to avoid recreating it on each render
  const updateURL = useCallback((params) => {
    // Create the new URL with current origin and pathname, plus the new search params
    const newUrl = new URL(window.location.origin + window.location.pathname);
    
    // For SEO purposes: don't add page=1 to URL to keep homepage clean
    const paramsToAdd = {...params};
    if (paramsToAdd.page === '1' && !paramsToAdd.tag) {
      delete paramsToAdd.page; // Remove page param when it's page 1 and no tag filter
    }
    
    // Add all remaining search params to the URL
    Object.entries(paramsToAdd).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newUrl.searchParams.set(key, value);
      }
    });
    
    // Use navigate instead of setSearchParams for better URL updates
    navigate(newUrl.pathname + newUrl.search, { 
      replace: false // Use push not replace so back button works properly
    });
  }, [navigate]);
  
  // Initialize tag state from URL params
  useEffect(() => {
    // Priority: URL parameter > location state
    if (tagParam) {
      setSelectedTag(tagParam);
    } else if (location.state && location.state.selectedTag) {
      // Set the tag from location state and update URL
      setSelectedTag(location.state.selectedTag);
      
      // Update URL to reflect the tag from state
      updateURL({
        tag: location.state.selectedTag,
        page: '1'
      });
    }
  }, [location, tagParam, updateURL]);

  useEffect(() => {
    // Function to fetch quotes
    const fetchQuotes = async (page = 1, tagFilter = null, loadingMore = false) => {
      try {
        if (!loadingMore) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        
        setError(null);
        
        // Build URL with appropriate filters
        let url = `/api/quotes?page=${page}&pageSize=10`;
        
        // Add tag filter if provided
        if (tagFilter) {
          url += `&tag=${encodeURIComponent(tagFilter)}`;
        }
        
        // Add cache-busting parameter to prevent caching issues
        const cacheBuster = new Date().getTime();
        url += `&_=${cacheBuster}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          if (response.status === 500) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(`Server error: ${errorData.details || errorData.error || 'Unknown server error'}`);
          }
          const text = await response.text();
          throw new Error(`API error (${response.status}): ${text}`);
        }
        
        const data = await response.json();
        
        // The structure is now { quotes: [...], pagination: {...} }
        if (!data.quotes || !Array.isArray(data.quotes)) {
          console.warn('Expected quotes array but got:', data);
          setQuotes([]);
          setPagination({
            currentPage: 1,
            totalPages: 0,
            pageSize: 10,
            totalQuotes: 0,
            hasMore: false
          });
        } else {
          setQuotes(data.quotes);
          setPagination(data.pagination || {
            currentPage: page,
            totalPages: 1,
            pageSize: 10,
            totalQuotes: data.quotes.length,
            hasMore: false
          });
        }
        
        if (loadingMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching quotes:', error);
        setError(error.message);
        setLoading(false);
        setLoadingMore(false);
      }
    };

    fetchQuotes(currentPage, selectedTag);
  }, [retryCount, currentPage, selectedTag]); // Retry when retryCount changes, page changes, or tag changes

  // Handle retry
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Keep tag filter if it exists
    const params = { page: newPage.toString() };
    if (selectedTag) {
      params.tag = selectedTag;
    }
    
    updateURL(params);
  };
  
  // Handle tag selection
  const handleTagClick = (tag) => {
    setSelectedTag(tag);
    updateURL({ tag, page: '1' });
  };
  
  // Handle clearing tag filter
  const handleClearTag = () => {
    setSelectedTag(null);
    
    // Navigate to clean quotes URL for SEO purposes
    navigate('/q/', { replace: true });
  };

  // We no longer need to filter quotes by tag client-side
  // because the API now does this for us
  const filteredQuotes = quotes;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600"></div>
          <span className="ml-3 text-gray-700">Loading claims...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <div className="flex flex-col space-y-4">
          <button 
            onClick={handleRetry}
            className="bg-slate-500 hover:bg-slate-600 text-white font-medium py-2 px-4 rounded-md"
          >
            Retry
          </button>
          
          <div className="bg-slate-100 border border-slate-300 text-slate-700 px-4 py-3 rounded-xl">
            <h3 className="font-bold mb-2">Troubleshooting Steps:</h3>
            <ol className="list-decimal list-inside space-y-1">
              <li>Check if the D1 database is properly connected in Cloudflare Pages</li>
              <li>Verify your D1 database has been initialized with schema and data</li>
              <li>Check Cloudflare Functions logs for detailed error information</li>
              <li>Try visiting <a href="/api/test" className="text-slate-600 hover:text-slate-800 underline" target="_blank" rel="noopener noreferrer">/api/test</a> to test API connectivity</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* SEO metadata and canonical URL */}
      <Helmet>
        <title>Political Claims - Cult of the Red Hat</title>
        <meta name="description" content="Explore political claims with analysis of democratic impact and rhetorical tactics." />
        <link rel="canonical" href={`${window.location.origin}/q`} />
        
        {/* Add pagination meta if needed */}
        {pagination.currentPage > 1 && (
          <>
            {pagination.currentPage > 2 && (
              <link 
                rel="prev" 
                href={`${window.location.origin}/q${selectedTag ? `?tag=${selectedTag}&page=${pagination.currentPage - 1}` : `?page=${pagination.currentPage - 1}`}`} 
              />
            )}
            {pagination.currentPage === 2 && (
              <link 
                rel="prev" 
                href={`${window.location.origin}/q${selectedTag ? `?tag=${selectedTag}` : ''}`} 
              />
            )}
          </>
        )}
        
        {pagination.hasMore && (
          <link 
            rel="next" 
            href={`${window.location.origin}/q${selectedTag ? `?tag=${selectedTag}&page=${pagination.currentPage + 1}` : `?page=${pagination.currentPage + 1}`}`} 
          />
        )}
      </Helmet>
      
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-2">Threats to Democracy: Claims</h1>
        <p className="text-gray-600 max-w-3xl mx-auto">
          Collection of political claims analyzed for factual accuracy and potential impact on democratic values and public institutions.
        </p>
      </div>

      {/* Show selected tag with a way to clear it */}
      {selectedTag && (
        <div className="mb-8">
          <button
            onClick={handleClearTag}
            className="text-slate-600 hover:text-slate-800 flex items-center"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to all claims
          </button>
          <h2 className="text-lg font-medium mt-2 text-slate-800">
            Claims tagged with "{selectedTag}"
          </h2>
        </div>
      )}

      
      {filteredQuotes.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No claims found. {selectedTag && 'Try selecting a different tag.'}</p>
          {!selectedTag && (
            <div className="bg-slate-100 border border-slate-300 text-slate-700 px-4 py-3 rounded-xl inline-block">
              <p>Your database may be empty. Try adding claims through the admin panel.</p>
              <Link to="/admin" className="text-slate-600 hover:text-slate-800 underline mt-2 inline-block">Go to Admin Panel</Link>
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Display quotes */}
          <div className="mb-8">
            {filteredQuotes.map(quote => (
              <EnhancedQuoteCard
                key={quote.id}
                quote={quote}
                onTagClick={handleTagClick}
              />
            ))}
          </div>
          
          {/* Pagination controls */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-between items-center border-t border-gray-200 pt-6 pb-8">
              {/* Page indicator */}
              <div className="text-sm text-gray-700">
                <span className="font-medium">Page {pagination.currentPage}</span> of <span className="font-medium">{pagination.totalPages}</span>
              </div>
              
              {/* Navigation buttons */}
              <div className="flex space-x-2">
                {/* Previous page button */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    pagination.currentPage <= 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-slate-700 bg-slate-100 hover:bg-slate-200'
                  }`}
                >
                  <ArrowLeftCircle size={18} className="mr-1" />
                  Previous
                </button>
                
                {/* Next page button */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasMore}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    !pagination.hasMore
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-slate-700 bg-slate-100 hover:bg-slate-200'
                  }`}
                >
                  Next
                  <ArrowRightCircle size={18} className="ml-1" />
                </button>
              </div>
            </div>
          )}
          
          {/* Loading more indicator */}
          {loadingMore && (
            <div className="flex justify-center items-center py-4">
              <Loader2 size={24} className="animate-spin text-slate-600 mr-2" />
              <span className="text-slate-600">Loading more...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Quotes;